import { DotPhraseTemplate, InputSize, WithId } from '@hb/shared'
import React, { DetailedHTMLProps, forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import { useDotPhrase } from '../../../../contexts/dotPhrase'
import { useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'
import { getCaretViewportPosition } from './caretCoordinates'
import { DotPhrasePicker } from './DotPhrasePicker'

// Returns the ".keyword" dot-phrase token ending at the caret, or null. The token is the
// trailing run of non-whitespace before the caret, and only counts when it starts with '.'
// (i.e. the '.' begins a word — field start or after whitespace). `/\S*$/` is anchored at the
// end, so this is O(token length), not O(field length) — important for long notes typed into
// on every keystroke.
export const getDotPhraseToken = (
  text: string,
  caret: number,
): { tokenStart: number; keyword: string } | null => {
  const before = text.slice(0, caret)
  const word = /\S*$/.exec(before)?.[0] ?? ''
  if (!word.startsWith('.')) return null
  return { tokenStart: before.length - word.length, keyword: word.slice(1) }
}

export const TextInputElement = forwardRef<
  HTMLInputElement,
  Omit<DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size'> & {
    withDotPhrases?: boolean
    size?: InputSize
  }
>(({ style, onKeyDown, onChange, onBlur, name, withDotPhrases, size: sizeProp, ...props }, ref) => {
  const size = useInputSize(sizeProp)
  // Dot phrases are opt-in per field — only active when the field sets `withDotPhrases`.
  // This avoids the picker/Tab/caret machinery (and any unwanted behavior) on every text input.
  const dotPhraseCtx = useDotPhrase()
  const dotPhrase = withDotPhrases ? dotPhraseCtx : null
  const [showPicker, setShowPicker] = useState(false)
  const [phraseSearch, setPhraseSearch] = useState('')
  // Keyboard-highlighted row in the picker.
  const [activeIndex, setActiveIndex] = useState(0)
  // Viewport coords of the caret, so the picker can float next to the cursor.
  const [caretPos, setCaretPos] = useState<{ top: number; left: number } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Read the latest picker state inside handlers without making them depend on it, so the
  // handlers keep a stable identity and the input's DOM listeners aren't swapped each render.
  const showPickerRef = useRef(showPicker)
  showPickerRef.current = showPicker
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  // Assigned after pickerItems is computed below; read at event time.
  const pickerItemsRef = useRef<WithId<DotPhraseTemplate>[]>([])

  // Memoized so the input's ref callback identity is stable across renders — otherwise
  // React detaches and re-attaches the DOM node on every single render (ref churn).
  const setMergedRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) ref.current = el
    },
    [ref],
  )

  const closePicker = useCallback(() => {
    setShowPicker(false)
    setPhraseSearch('')
    setCaretPos(null)
    setActiveIndex(0)
  }, [])

  const handlePickerSelect = useCallback(
    (body: string) => {
      const el = inputRef.current
      if (!el) return
      // Replace only the ".keyword" token at the caret with the template body, preserving the
      // rest of the field, then drop the caret at the end of the inserted text.
      const caret = el.selectionStart ?? el.value.length
      const token = getDotPhraseToken(el.value, caret)
      const tokenStart = token ? token.tokenStart : caret
      const newValue = el.value.slice(0, tokenStart) + body + el.value.slice(caret)
      const newCaret = tokenStart + body.length
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
      setter?.call(el, newValue)
      el.dispatchEvent(new Event('input', { bubbles: true }))
      requestAnimationFrame(() => el.setSelectionRange(newCaret, newCaret))
      closePicker()
    },
    [closePicker],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // While the picker is open, take over navigation keys (Tab no longer leaves the field).
      const items = pickerItemsRef.current
      if (dotPhrase && showPickerRef.current && items.length > 0) {
        const n = items.length
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setActiveIndex(i => (i + 1) % n)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setActiveIndex(i => (i - 1 + n) % n)
          return
        }
        if (e.key === 'Tab') {
          e.preventDefault()
          setActiveIndex(i => (i + (e.shiftKey ? -1 : 1) + n) % n)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          const item = items[Math.min(activeIndexRef.current, n - 1)]
          if (item) handlePickerSelect(item.body)
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          closePicker()
          return
        }
      }
      // Picker closed: Tab expands the keyword before the caret (existing behavior).
      if (e.key === 'Tab' && dotPhrase && e.currentTarget) {
        const el = e.currentTarget
        const result = dotPhrase.tryExpand(el.value, el.selectionStart ?? 0, name ?? '')
        if (result.matched) {
          e.preventDefault()
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
          setter?.call(el, result.newValue)
          el.dispatchEvent(new Event('input', { bubbles: true }))
          const cursor = result.newCursor
          requestAnimationFrame(() => el.setSelectionRange(cursor, cursor))
          closePicker()
        }
      }
      onKeyDown?.(e)
    },
    [dotPhrase, onKeyDown, name, handlePickerSelect, closePicker],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      if (!dotPhrase) return
      const el = e.target
      const caret = el.selectionStart ?? el.value.length
      // Trigger on a '.' that starts a word (field start or after whitespace), anywhere in
      // the field — matches how dot phrases are actually typed (e.g. ".hpi" mid-sentence),
      // not just when the whole field is exactly ".". The common case (no such token at the
      // caret) makes no state update at all.
      const token = getDotPhraseToken(el.value, caret)
      if (token) {
        if (!showPickerRef.current) setShowPicker(true)
        setPhraseSearch(token.keyword)
        setActiveIndex(0) // highlight the top match as the list refilters
        // Anchor the picker just below the caret (at the start of the '.' token).
        setCaretPos(getCaretViewportPosition(el, token.tokenStart))
      } else if (showPickerRef.current) {
        closePicker()
      }
    },
    [dotPhrase, onChange, closePicker],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      closePicker()
      onBlur?.(e)
    },
    [onBlur, closePicker],
  )

  const pickerItems = useMemo(() => {
    if (!dotPhrase || !showPicker) return []
    return dotPhrase.templates
      .filter(
        t =>
          !phraseSearch ||
          t.keyword.toLowerCase().includes(phraseSearch.toLowerCase()) ||
          t.description?.toLowerCase().includes(phraseSearch.toLowerCase()),
      )
      .slice(0, 8)
  }, [dotPhrase, showPicker, phraseSearch])
  pickerItemsRef.current = pickerItems

  const inputStyle = useMemo(
    () => ({ ...baseInputStyle(size, props.disabled), ...style }),
    [size, props.disabled, style],
  )

  const input = (
    <input
      ref={setMergedRef}
      style={inputStyle}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onBlur={handleBlur}
      name={name}
      {...props}
    />
  )

  // Keep the <input> at a STABLE tree position (always the first child here) and toggle the
  // picker as a sibling. The picker floats at the caret via `position: fixed`. Conditionally
  // wrapping the input in a new element instead would move it in the tree → remount the DOM
  // node → drop focus + fire blur, which closed the picker the instant '.' was typed. A
  // fragment avoids both the remount and an extra styled wrapper on every keystroke.
  if (!dotPhrase) return input

  return (
    <>
      {input}
      {showPicker && pickerItems.length > 0 && (
        <DotPhrasePicker
          items={pickerItems}
          onSelect={handlePickerSelect}
          coords={caretPos}
          activeIndex={Math.min(activeIndex, pickerItems.length - 1)}
          onHoverIndex={setActiveIndex}
        />
      )}
    </>
  )
})
